/**
 * maprimerenov-content.ts
 * ─────────────────────────────────────────────────────────────────
 * Source unique du contenu éditorial de la page-guide
 * /particuliers/aides/maprimerenov.
 *
 * Toutes les données factuelles (plafonds, montants par geste,
 * étapes de la démarche, FAQ) sont centralisées
 * ici pour que la page reste 100 % composition de blocs (AGENTS §3).
 *
 * RÈGLES ÉDITORIALES (AGENTS §8) :
 *  - Versions officielles autorisées : MaPrimeRénov',
 *    MaPrimeRénov' Copropriétés, MaPrimeRénov' Décarbonation,
 *    MaPrimeRénov' Ampleur.
 *  - MaPrimeRénov' = résidentiel principal uniquement.
 *  - Versement par l'organisme officiel.
 *  - Barèmes revalorisés annuellement.
 */

// ════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════

export interface MprSummaryPoint {
  text: string;
}

export interface MprVolet {
  id: 'decarbonation' | 'ampleur' | 'copropriete';
  title: string;
  /** Sous-titre lisible (ex: "rénovation par geste") */
  caption: string;
  /** Description courte du volet */
  description: string;
  /** Public visé (1 ligne) */
  audience: string;
  /** Trigger éligibilité ("À retenir") */
  highlight: string;
  iconName: string;
  /** Lien interne pour approfondir */
  href?: string;
}

export interface MprBeneficiaire {
  iconName: string;
  label: string;
  detail: string;
}

export interface MprTravauxCategory {
  iconName: string;
  title: string;
  items: string[];
  note?: string;
}

export interface MprIncomeBand {
  /** Nom de la catégorie */
  label: string;
  /** Code couleur (Bleu, Jaune, Violet, Rose) */
  color: 'bleu' | 'jaune' | 'violet' | 'rose';
  /** Plafond 1 pers (en €) */
  p1: string;
  p2: string;
  p3: string;
  p4: string;
  p5: string;
  /** Incrément par personne supplémentaire */
  perExtra: string;
}

export interface MprMontantGeste {
  travaux: string;
  /** Plafond maximal accordé selon catégorie de revenus la plus avantageuse */
  plafond: string;
  /** Note optionnelle (ex: surface plafonnée, m²) */
  note?: string;
}

export interface MprAmpleurRow {
  /** Description du gain visé */
  gain: string;
  /** Plafond de dépenses éligibles HT */
  depensesHt: string;
  /** Montant max par profil */
  bleu: string;
  jaune: string;
  violet: string;
  rose: string;
}

export interface MprMarPriseEnCharge {
  /** Profil de revenus */
  profil: string;
  color: 'bleu' | 'jaune' | 'violet' | 'rose';
  /** Taux de prise en charge */
  taux: string;
}

export interface MprMarMission {
  iconName: string;
  title: string;
  description: string;
}

export interface MprDemarche {
  /** Numéro d'étape (1, 2, 3...) */
  step: number;
  iconName: string;
  title: string;
  description: string;
}

export interface MprCumul {
  /** Nom de l'aide cumulable */
  aide: string;
  /** Description courte du cumul */
  description: string;
  iconName: string;
  /** Couleur d'accent pour la chip */
  tone: 'brand' | 'accent' | 'sage' | 'lavender' | 'ivory';
  /** Lien interne vers la page dédiée si elle existe */
  href?: string;
}

export interface MprFaqItem {
  question: string;
  answer: string;
}

// ════════════════════════════════════════════════════════════════
// HERO + RÉSUMÉ
// ════════════════════════════════════════════════════════════════

export const MPR_HERO = {
  eyebrow: "L'aide de l'État pour vos travaux",
  /** Titre H1 (segment 1) */
  titlePrimary: "MaPrimeRénov'",
  /** Titre H1 (segment 2 — gradient) */
  titleAccent: 'le guide complet 2026',
  /** Lead sous le H1 */
  lead:
    "L'aide principale pour financer vos travaux de rénovation. Son montant dépend de vos revenus et de ce que vous faites : plus vos ressources sont modestes, plus l'aide est élevée. On vous explique comment l'obtenir et la cumuler.",
  /** Montant indicatif affiché dans la sidecard */
  amountHeadline: "Jusqu'à 63 000 €",
  amountSubline:
    "Cumul possible avec la prime énergie et le prêt sans intérêt, selon vos revenus, vos travaux et le gain énergétique.",
  /** Liens rapides sous le hero */
  microPromise: 'Barème 2026, sans inscription, estimation gratuite',
} as const;

export const MPR_SUMMARY: MprSummaryPoint[] = [
  {
    text:
      "MaPrimeRénov' est une aide forfaitaire versée par l'organisme national de l'habitat pour financer la rénovation énergétique de votre résidence principale.",
  },
  {
    text:
      "Son montant dépend de votre catégorie de revenus (4 profils), de la nature des travaux et du gain énergétique apporté.",
  },
  {
    text:
      "Trois volets coexistent : le parcours par geste, le parcours rénovation globale et le volet copropriétés.",
  },
  {
    text:
      "Travaux réalisés par un professionnel certifié, demande déposée avant signature du devis : ce sont les deux conditions pour ne pas se voir refuser le dossier.",
  },
];

// ════════════════════════════════════════════════════════════════
// LES 3 VOLETS
// ════════════════════════════════════════════════════════════════

export const MPR_VOLETS: MprVolet[] = [
  {
    id: 'decarbonation',
    title: "MaPrimeRénov' Décarbonation",
    caption: 'Parcours par geste',
    description:
      "Aide forfaitaire pour remplacer votre système de chauffage ou de production d'eau chaude par une solution décarbonée (pompe à chaleur, chaudière biomasse, solaire thermique, raccordement réseau de chaleur).",
    audience: 'Propriétaires occupants ou bailleurs, 1 ou 2 gestes ciblés sur le chauffage / eau chaude',
    highlight: 'Idéal pour remplacer une chaudière fioul ou gaz par une pompe à chaleur.',
    iconName: 'flame',
  },
  {
    id: 'ampleur',
    title: "MaPrimeRénov' Ampleur",
    caption: 'Parcours accompagné, rénovation globale',
    description:
      "Aide bonifiée pour les rénovations d'ampleur permettant un gain d'au moins 2 crans sur l'étiquette énergie, incluant au moins deux gestes d'isolation. Accompagnement obligatoire par un conseiller agréé.",
    audience: 'Propriétaires occupants ou bailleurs, projet pluri-travaux avec diagnostic énergétique',
    highlight: "Sortie de passoire thermique (étiquette F ou G) : jusqu'à 63 000 € d'aides cumulées.",
    iconName: 'layers',
  },
  {
    id: 'copropriete',
    title: "MaPrimeRénov' Copropriétés",
    caption: 'Versée au syndicat des copropriétaires',
    description:
      "Aide collective pour les travaux réalisés sur les parties communes : isolation, ventilation, changement du chauffage collectif, raccordement à un réseau de chaleur. Demande portée par le syndic après vote en assemblée générale.",
    audience: 'Syndicats de copropriétaires, syndics professionnels, conseils syndicaux',
    highlight: "Jusqu'à 30 % du montant des travaux pris en charge pour le syndicat.",
    iconName: 'building-2',
    href: '/coproprietes/calculer-mes-aides',
  },
];

// ════════════════════════════════════════════════════════════════
// QUI PEUT EN BÉNÉFICIER ?
// ════════════════════════════════════════════════════════════════

export const MPR_BENEFICIAIRES: MprBeneficiaire[] = [
  {
    iconName: 'home',
    label: 'Propriétaires occupants',
    detail:
      "Vous occupez le logement comme résidence principale (au moins 8 mois par an).",
  },
  {
    iconName: 'users',
    label: 'Propriétaires bailleurs',
    detail:
      "Vous louez le logement en résidence principale pour au moins 6 ans après les travaux.",
  },
  {
    iconName: 'file-signature',
    label: 'Propriétaires en cours d\'acquisition',
    detail:
      "Vous avez signé un compromis de vente : vous pouvez déjà préparer votre dossier.",
  },
  {
    iconName: 'building-2',
    label: 'Copropriétés et syndicats',
    detail:
      'La copropriété peut demander le volet collectif pour les travaux en parties communes.',
  },
];

export const MPR_LOGEMENT_CRITERES: string[] = [
  'Résidence principale (occupation au moins 8 mois par an)',
  'Logement achevé depuis plus de 15 ans (France métropolitaine) ou plus de 2 ans (Outre-mer)',
  'Situé en France métropolitaine ou Outre-mer (Guadeloupe, Martinique, Guyane, Mayotte, La Réunion)',
  "Exception : logement de moins de 15 ans accepté pour le remplacement d'une chaudière fioul ou charbon",
];

// ════════════════════════════════════════════════════════════════
// TRAVAUX ÉLIGIBLES
// ════════════════════════════════════════════════════════════════

export const MPR_TRAVAUX: MprTravauxCategory[] = [
  {
    iconName: 'flame',
    title: 'Chauffage & eau chaude',
    items: [
      'Pompe à chaleur air-eau ou hybride',
      'Pompe à chaleur géothermique',
      'Chauffe-eau solaire individuel',
      'Système solaire combiné',
      'Chauffe-eau thermodynamique',
      'Poêle ou chaudière à bûches / granulés',
      'Raccordement à un réseau de chaleur renouvelable',
    ],
    note:
      "Depuis janvier 2026, les chaudières biomasse ne sont plus éligibles en geste isolé (uniquement en rénovation globale).",
  },
  {
    iconName: 'snowflake',
    title: 'Isolation thermique',
    items: [
      "Isolation des murs par l'extérieur (surface limitée à 100 m²)",
      "Isolation des murs par l'intérieur",
      'Isolation des rampants de toiture ou des plafonds de combles',
      'Isolation des toitures-terrasses',
      'Isolation des planchers bas (en rénovation globale)',
      'Remplacement de simple vitrage par du double ou triple vitrage',
    ],
    note:
      "Depuis janvier 2026, l'isolation des murs n'est plus éligible en geste isolé (réservée à la rénovation globale).",
  },
  {
    iconName: 'wind',
    title: 'Autres travaux',
    items: [
      "Diagnostic énergétique (hors obligation réglementaire)",
      "VMC double flux (avec geste d'isolation associé obligatoire)",
      "Dépose d'une cuve à fioul (avec installation d'un chauffage décarboné)",
      'Protection contre le rayonnement solaire (Outre-mer uniquement)',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// PLAFONDS DE RESSOURCES 2026
// ════════════════════════════════════════════════════════════════

export const MPR_INCOME_IDF: MprIncomeBand[] = [
  {
    label: 'Très modestes',
    color: 'bleu',
    p1: '≤ 24 031 €',
    p2: '≤ 35 270 €',
    p3: '≤ 42 357 €',
    p4: '≤ 49 455 €',
    p5: '≤ 56 580 €',
    perExtra: '+ 7 116 €',
  },
  {
    label: 'Modestes',
    color: 'jaune',
    p1: '≤ 29 253 €',
    p2: '≤ 42 933 €',
    p3: '≤ 51 594 €',
    p4: '≤ 60 208 €',
    p5: '≤ 68 877 €',
    perExtra: '+ 8 663 €',
  },
  {
    label: 'Intermédiaires',
    color: 'violet',
    p1: '≤ 40 851 €',
    p2: '≤ 60 051 €',
    p3: '≤ 71 846 €',
    p4: '≤ 84 562 €',
    p5: '≤ 96 817 €',
    perExtra: '+ 12 257 €',
  },
  {
    label: 'Supérieurs',
    color: 'rose',
    p1: '> 40 851 €',
    p2: '> 60 051 €',
    p3: '> 71 846 €',
    p4: '> 84 562 €',
    p5: '> 96 817 €',
    perExtra: '+ 12 257 €',
  },
];

export const MPR_INCOME_HORS_IDF: MprIncomeBand[] = [
  {
    label: 'Très modestes',
    color: 'bleu',
    p1: '≤ 17 363 €',
    p2: '≤ 25 393 €',
    p3: '≤ 30 540 €',
    p4: '≤ 35 676 €',
    p5: '≤ 40 835 €',
    perExtra: '+ 5 151 €',
  },
  {
    label: 'Modestes',
    color: 'jaune',
    p1: '≤ 22 259 €',
    p2: '≤ 32 553 €',
    p3: '≤ 39 148 €',
    p4: '≤ 45 735 €',
    p5: '≤ 52 348 €',
    perExtra: '+ 6 598 €',
  },
  {
    label: 'Intermédiaires',
    color: 'violet',
    p1: '≤ 31 185 €',
    p2: '≤ 45 842 €',
    p3: '≤ 55 196 €',
    p4: '≤ 64 550 €',
    p5: '≤ 73 907 €',
    perExtra: '+ 9 357 €',
  },
  {
    label: 'Supérieurs',
    color: 'rose',
    p1: '> 31 185 €',
    p2: '> 45 842 €',
    p3: '> 55 196 €',
    p4: '> 64 550 €',
    p5: '> 73 907 €',
    perExtra: '+ 9 357 €',
  },
];

// ════════════════════════════════════════════════════════════════
// MONTANTS PAR TYPE DE TRAVAUX (Parcours Décarbonation, plafonds max)
// ════════════════════════════════════════════════════════════════

export const MPR_MONTANTS_GESTES: MprMontantGeste[] = [
  { travaux: 'Pompe à chaleur géothermique', plafond: "Jusqu'à 11 000 €" },
  { travaux: 'Système solaire combiné', plafond: "Jusqu'à 10 000 €" },
  { travaux: 'Pompe à chaleur air-eau ou hybride', plafond: "Jusqu'à 5 000 €" },
  { travaux: 'VMC double flux', plafond: "Jusqu'à 2 500 €", note: "Geste d'isolation associé obligatoire" },
  {
    travaux: "Partie thermique d'un équipement PVT (photovoltaïque hybride)",
    plafond: "Jusqu'à 2 500 €",
  },
  { travaux: 'Poêle à bûches ou à granulés', plafond: "Jusqu'à 1 250 €" },
  { travaux: 'Raccordement à un réseau de chaleur ou de froid', plafond: "Jusqu'à 1 200 €" },
  { travaux: 'Chauffe-eau thermodynamique', plafond: "Jusqu'à 1 200 €" },
  { travaux: 'Isolation des combles aménagés / rampants', plafond: "Jusqu'à 25 €/m²" },
  { travaux: 'Isolation de la toiture-terrasse', plafond: "Jusqu'à 75 €/m²" },
  { travaux: 'Changement de fenêtres', plafond: "Jusqu'à 100 €/fenêtre" },
];

// ════════════════════════════════════════════════════════════════
// RÉNOVATION GLOBALE (gain énergétique × profil)
// ════════════════════════════════════════════════════════════════

export const MPR_AMPLEUR_ROWS: MprAmpleurRow[] = [
  {
    gain: "Saut de 2 crans sur l'étiquette énergie",
    depensesHt: '30 000 €',
    bleu: '24 000 € max',
    jaune: '18 000 € max',
    violet: '13 500 € max',
    rose: '3 000 € max',
  },
  {
    gain: "Saut de 3 crans ou plus sur l'étiquette énergie",
    depensesHt: '40 000 €',
    bleu: '32 000 € max',
    jaune: '24 000 € max',
    violet: '18 000 € max',
    rose: '4 000 € max',
  },
];

// ════════════════════════════════════════════════════════════════
// CONSEILLER AGRÉÉ
// ════════════════════════════════════════════════════════════════

export const MPR_MAR_MISSIONS: MprMarMission[] = [
  {
    iconName: 'file-search',
    title: 'Évaluation personnalisée',
    description:
      "Diagnostic énergétique du logement, identification de l'étiquette de départ et du gain à viser.",
  },
  {
    iconName: 'clipboard-list',
    title: 'Conseils adaptés',
    description:
      "Recommandation des travaux les plus efficaces selon le bâti, le climat et votre situation.",
  },
  {
    iconName: 'handshake',
    title: 'Aide à la décision',
    description:
      "Priorisation des travaux par rapport budgétaire et calendrier (gain énergétique × coût × confort).",
  },
  {
    iconName: 'clipboard-check',
    title: 'Préparation des travaux',
    description:
      "Constitution du dossier, vérification des devis, suivi administratif.",
  },
  {
    iconName: 'shield-check',
    title: 'Suivi après travaux',
    description:
      "Contrôle des réalisations conformément aux devis et accompagnement jusqu'au versement de la prime.",
  },
];

export const MPR_MAR_PRISE_EN_CHARGE: MprMarPriseEnCharge[] = [
  { profil: 'Très modestes', color: 'bleu', taux: '100 %' },
  { profil: 'Modestes', color: 'jaune', taux: '80 %' },
  { profil: 'Intermédiaires', color: 'violet', taux: '40 %' },
  { profil: 'Supérieurs', color: 'rose', taux: '20 %' },
];

// ════════════════════════════════════════════════════════════════
// CUMUL AVEC LES AUTRES AIDES
// ════════════════════════════════════════════════════════════════

export const MPR_CUMULS: MprCumul[] = [
  {
    aide: 'Prime énergie',
    description:
      "Prime versée par les fournisseurs d'énergie. Sans condition de ressources, cumulable à 100 %.",
    iconName: 'sparkles',
    tone: 'accent',
    href: '/particuliers/aides/cee',
  },
  {
    aide: 'Éco-PTZ',
    description:
      "Prêt à taux zéro jusqu'à 50 000 € pour financer la part restant à votre charge. Aucune condition de ressources.",
    iconName: 'banknote',
    tone: 'brand',
    href: '/particuliers/aides/eco-ptz',
  },
  {
    aide: 'TVA à 5,5 %',
    description:
      "Taux réduit appliqué automatiquement par l'artisan sur les travaux d'amélioration énergétique (logement de plus de 2 ans).",
    iconName: 'percent',
    tone: 'sage',
    href: '/particuliers/aides/tva-reduite',
  },
  {
    aide: 'Coup de pouce',
    description:
      'Prime majorée sur certaines opérations ciblées (remplacement chauffage fossile, isolation).',
    iconName: 'flame',
    tone: 'lavender',
    href: '/particuliers/aides/coup-de-pouce',
  },
  {
    aide: 'Aides locales',
    description:
      'Aides des Régions, Départements, métropoles ou intercommunalités. Variables selon votre code postal.',
    iconName: 'landmark',
    tone: 'ivory',
  },
];

/** Plafonds de cumul (montant total d'aides plafonné en % des dépenses HT) */
export const MPR_ECRETEMENT: Array<{ profil: string; color: 'bleu' | 'jaune' | 'violet' | 'rose'; taux: string }> = [
  { profil: 'Très modestes', color: 'bleu', taux: '80 % des dépenses HT' },
  { profil: 'Modestes', color: 'jaune', taux: '80 % des dépenses HT' },
  { profil: 'Intermédiaires', color: 'violet', taux: '60 % des dépenses HT' },
  { profil: 'Supérieurs', color: 'rose', taux: '40 % des dépenses HT' },
];

// ════════════════════════════════════════════════════════════════
// DÉMARCHES — ORDRE STRICT À RESPECTER
// ════════════════════════════════════════════════════════════════

export const MPR_DEMARCHES: MprDemarche[] = [
  {
    step: 1,
    iconName: 'calculator',
    title: 'Simuler votre prime',
    description:
      'Estimez votre profil de revenus, identifiez les travaux éligibles et calculez le montant prévisionnel avec notre calculateur.',
  },
  {
    step: 2,
    iconName: 'hard-hat',
    title: 'Faire établir un devis par un professionnel certifié',
    description:
      "L'entreprise doit être certifiée pour la catégorie de travaux concernée. Ne signez surtout pas le devis avant l'étape 3.",
  },
  {
    step: 3,
    iconName: 'file-up',
    title: 'Déposer votre dossier en ligne',
    description:
      "Création de votre compte, dépôt des devis, justificatifs de revenus (avis d'imposition) et caractéristiques du logement. Pour la rénovation globale, le dépôt passe par votre conseiller agréé.",
  },
  {
    step: 4,
    iconName: 'shield-check',
    title: "Attendre la notification d'accord",
    description:
      "Votre dossier est instruit sous 1 à 2 mois. Vous recevez un courrier précisant le montant accordé. C'est seulement après cette notification que vous pouvez signer le devis et lancer les travaux.",
  },
  {
    step: 5,
    iconName: 'wallet',
    title: 'Faire réaliser les travaux puis demander le versement',
    description:
      "Une fois les travaux terminés, vous transmettez les factures en ligne. Le versement est effectué par virement bancaire, sous 2 à 4 mois en moyenne.",
  },
];

// ════════════════════════════════════════════════════════════════
// FAQ
// ════════════════════════════════════════════════════════════════

export const MPR_FAQ: MprFaqItem[] = [
  {
    question: "Quel est le montant en 2026 ?",
    answer:
      "Le montant dépend de votre profil de revenus et du type de travaux. Exemples : jusqu'à 5 000 € pour une pompe à chaleur air-eau, jusqu'à 11 000 € pour une PAC géothermique, jusqu'à 25 €/m² pour l'isolation des combles. En rénovation globale, le cumul peut atteindre 32 000 €. Notre calculateur croise votre situation et vos travaux pour un chiffrage précis.",
  },
  {
    question: "Cette aide est-elle cumulable avec la prime énergie ?",
    answer:
      "Oui, intégralement. Les deux dispositifs sont conçus pour se cumuler. Sur une même opération (ex. pompe à chaleur), vous pouvez percevoir les deux aides, plus le prêt sans intérêt et la TVA à 5,5 %. Un plafond global s'applique : 80 % des dépenses pour les ménages modestes, 60 % pour les intermédiaires, 40 % pour les supérieurs.",
  },
  {
    question: "Quelle est la différence entre le parcours par geste et la rénovation globale ?",
    answer:
      "Le parcours par geste finance un ou deux travaux ciblés sur le chauffage ou l'eau chaude. La rénovation globale concerne les projets qui visent un gain d'au moins 2 crans sur l'étiquette énergie et incluent au moins deux gestes d'isolation. Ce second parcours ouvre droit à des montants plus élevés mais impose l'intervention d'un conseiller agréé et un diagnostic énergétique.",
  },
  {
    question: "Suis-je obligé de passer par un conseiller agréé ?",
    answer:
      "Oui pour la rénovation globale (parcours accompagné). Le conseiller est facultatif pour le parcours par geste. Son coût est partiellement pris en charge : 100 % pour les ménages très modestes, 80 % pour les modestes, 40 % pour les intermédiaires, 20 % pour les supérieurs.",
  },
  {
    question: "Dois-je fournir un diagnostic énergétique ?",
    answer:
      "Pour le parcours par geste, cette obligation a été repoussée à 2027 en métropole. Pour la rénovation globale, un diagnostic énergétique reste obligatoire (réalisé par un professionnel certifié, intégré au parcours via votre conseiller).",
  },
  {
    question: 'Quel est le délai de versement de la prime ?',
    answer:
      "Comptez environ 2 mois pour l'instruction de votre dossier, puis 2 à 4 mois après validation pour le versement effectif (virement bancaire). Le versement intervient une fois les travaux terminés et les factures déposées en ligne.",
  },
  {
    question: 'Que se passe-t-il si je suis locataire ?',
    answer:
      "Cette aide n'est pas accessible aux locataires. C'est au propriétaire bailleur de la demander (en s'engageant à louer le bien en résidence principale pendant au moins 6 ans). Si vous êtes locataire, parlez-en à votre propriétaire.",
  },
  {
    question: "Comment Solution Éco France peut-elle m'accompagner ?",
    answer:
      "Notre rôle : croiser les différentes aides pour réduire votre reste à charge. Si vous passez par notre réseau d'artisans partenaires, la prime énergie est déduite directement du devis (zéro avance sur ce volet). L'aide de l'État reste versée après dépôt en ligne, et nous vous accompagnons dans le montage du dossier.",
  },
];
